import { useEffect, useState } from "react";
import { TrendingUp, Eye, Users, MousePointer, Sparkles } from "lucide-react";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  Tooltip,
} from "recharts";

export default function DashboardTrafficMobile() {
  const [month, setMonth] = useState("ALL");
  const [tab, setTab] = useState("overview");

  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [topPages, setTopPages] = useState([]);

  const token = () => localStorage.getItem("token");
  const kode_toko = () => localStorage.getItem("kode_toko");

  const formatNumber = (num) =>
    new Intl.NumberFormat("id-ID").format(num || 0);

  const getMonthFormat = (m) => {
    if (m === "ALL") return "ALL";
    const year = new Date().getFullYear();
    return `${year}-${String(m).padStart(2, "0")}`;
  };

  /* ================= FETCH SUMMARY TRAFFIC ================= */
  const fetchSummary = async () => {
    try {
      const res = await fetch(
        `/api/traffic/summary?bulan=${getMonthFormat(month)}&kode_toko=${kode_toko()}`,
        {
          headers: {
            "x-api-key": "SECRET123456",
            Authorization: `Bearer ${token()}`,
          },
        }
      );

      const json = await res.json();
      const row = json.data?.[0];

      setSummary({
        visitors: Number(row?.visitors || 0),
        page_views: Number(row?.page_views || 0),
        bounce_rate: Number(row?.bounce_rate || 0),
        ctr: Number(row?.ctr || 0),
      });
    } catch (err) {
      console.log(err);
    }
  };

  /* ================= FETCH CHART ================= */
  const fetchChart = async () => {
    try {
      const res = await fetch(
        `/api/traffic/chart?bulan=${getMonthFormat(month)}&kode_toko=${kode_toko()}`,
        {
          headers: {
            "x-api-key": "SECRET123456",
            Authorization: `Bearer ${token()}`,
          },
        }
      );

      const json = await res.json();

      setChartData(
        (json.data || []).map((i) => ({
          date: i.date?.split("T")[0],
          visitors: Number(i.visitors),
        }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  /* ================= FETCH TOP PAGES ================= */
  const fetchTopPages = async () => {
    try {
      const res = await fetch(
        `/api/traffic/top-pages?bulan=${getMonthFormat(month)}&kode_toko=${kode_toko()}`,
        {
          headers: {
            "x-api-key": "SECRET123456",
            Authorization: `Bearer ${token()}`,
          },
        }
      );

      const json = await res.json();

      setTopPages(
        (json.data || []).map((i) => ({
          page: i.page,
          views: Number(i.views),
        }))
      );
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchChart();
    fetchTopPages();
  }, [month]);

  const stats = [
    {
      title: "Visitors",
      value: formatNumber(summary?.visitors),
      icon: Users,
      color: "from-indigo-500 to-blue-500",
    },
    {
      title: "Page Views",
      value: formatNumber(summary?.page_views),
      icon: Eye,
      color: "from-emerald-500 to-green-500",
    },
    {
      title: "Bounce Rate",
      value: `${summary?.bounce_rate || 0}%`,
      icon: TrendingUp,
      color: "from-orange-500 to-amber-500",
    },
    {
      title: "CTR",
      value: `${summary?.ctr || 0}%`,
      icon: MousePointer,
      color: "from-pink-500 to-rose-500",
    },
  ];

  return (
    <div className="min-h-screen bg-[#f4f7fb] md:hidden pb-24 overflow-x-hidden">

      {/* HEADER */}
      <div className="px-2 pt-5">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-800">
            Traffic Dashboard
          </h1>

          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-white border border-gray-200 rounded-xl px-2 py-2 text-xs shadow-sm"
          >
            <option value="ALL">All Months</option>
            {[...Array(12)].map((_, i) => (
              <option key={i} value={i + 1}>
                {new Date(0, i).toLocaleString("id-ID", {
                  month: "long",
                })}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* HERO */}
      <div className="px-2 mt-4">
        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-500 p-5 text-white shadow-xl">

          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-white/10" />

          <div className="relative z-10">
            <div className="flex items-center gap-2 text-xs text-indigo-100">
              <Sparkles size={14} />
              Website Traffic
            </div>

            <h2 className="mt-3 text-3xl font-bold tracking-tight">
              {formatNumber(summary?.visitors)}
            </h2>

            <div className="mt-4 flex gap-2 text-sm">
              <div className="rounded-full bg-white/20 px-2 py-1">
                {summary?.page_views || 0} views
              </div>
              <div className="rounded-full bg-white/20 px-2 py-1">
                CTR {summary?.ctr || 0}%
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 gap-2.5 px-2 mt-4">
        {stats.map((item, i) => {
          const Icon = item.icon;

          return (
            <div
              key={i}
              className="bg-white rounded-2xl p-3.5 shadow-sm border border-gray-100"
            >
              <div
                className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white`}
              >
                <Icon size={20} />
              </div>

              <p className="text-xs text-gray-400 mt-3">
                {item.title}
              </p>

              <h3 className="mt-1 font-bold text-gray-800 text-sm">
                {item.value}
              </h3>
            </div>
          );
        })}
      </div>

      {/* TABS */}
      <div className="px-2 mt-5">
        <div className="bg-white rounded-2xl p-1 flex shadow-sm border">
          {["overview", "pages"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-xl text-xs font-medium ${
                tab === t
                  ? "bg-indigo-600 text-white"
                  : "text-gray-500"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* OVERVIEW CHART */}
      {tab === "overview" && (
        <div className="px-2 mt-5">
          <div className="bg-white rounded-[28px] p-3 shadow-sm border">

            <h3 className="font-semibold text-gray-800 mb-3">
              Traffic Trend
            </h3>

            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <XAxis dataKey="date" hide />
                <Tooltip />

                <Area
                  type="monotone"
                  dataKey="visitors"
                  stroke="#6366f1"
                  fill="url(#colorUv)"
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>

          </div>
        </div>
      )}

      {/* TOP PAGES */}
      {tab === "pages" && (
        <div className="px-2 mt-5 space-y-3">
          {topPages.map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-3.5 shadow-sm border flex justify-between"
            >
              <div>
                <h3 className="font-semibold text-sm text-gray-800">
                  {item.page}
                </h3>
                <p className="text-xs text-gray-400">
                  Top Page
                </p>
              </div>

              <div className="font-bold text-indigo-600 text-sm">
                {item.views}
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}